// Helpers that touch parts of the Cognito API not yet exposed by Amplify v6.16.

import {
  CognitoIdentityProviderClient,
  GetUserCommand,
  SetUserMFAPreferenceCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { fetchAuthSession, fetchMFAPreference } from 'aws-amplify/auth';
import awsConfig from './awsConfig';

const REGION = 'us-east-2';
const CLIENT_ID = awsConfig.Auth.Cognito.userPoolClientId;

const client = new CognitoIdentityProviderClient({ region: REGION });

// Toggle the WebAuthn MFA preference on the signed-in user.
// Amplify v6.16's updateMFAPreference only accepts sms/totp/email, so we call the raw SDK
// to set WebAuthnMfaSettings. Cognito requires all other MFA preferences to be passed
// in the same call (otherwise it treats omitted ones as disabled, which trips the
// "WebAuthn MFA requires enabling an additional MFA setting" guard).
export async function setPasskeyMfaEnabled(enabled) {
  const [session, currentPref] = await Promise.all([
    fetchAuthSession(),
    fetchMFAPreference(),
  ]);
  const accessToken = session?.tokens?.accessToken?.toString();
  if (!accessToken) throw new Error('No active session — cannot update MFA preference.');

  const enabledList = currentPref?.enabled || [];
  const preferred = currentPref?.preferred;

  await client.send(new SetUserMFAPreferenceCommand({
    AccessToken: accessToken,
    SMSMfaSettings: {
      Enabled: enabledList.includes('SMS'),
      PreferredMfa: preferred === 'SMS',
    },
    SoftwareTokenMfaSettings: {
      Enabled: enabledList.includes('TOTP'),
      PreferredMfa: preferred === 'TOTP',
    },
    EmailMfaSettings: {
      Enabled: enabledList.includes('EMAIL'),
      PreferredMfa: preferred === 'EMAIL',
    },
    WebAuthnMfaSettings: { Enabled: enabled },
  }));
}

// Whether WebAuthn MFA is on for the signed-in user. Amplify's fetchMFAPreference drops
// WEB_AUTHN_MFA from the enabled list, so read it from GetUser directly. A registered
// passkey alone doesn't make Cognito challenge anyone — only this setting does.
export async function fetchPasskeyMfaEnabled() {
  const session = await fetchAuthSession();
  const accessToken = session?.tokens?.accessToken?.toString();
  if (!accessToken) return false;

  const { UserMFASettingList } = await client.send(new GetUserCommand({ AccessToken: accessToken }));
  return (UserMFASettingList || []).includes('WEB_AUTHN_MFA');
}
